import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { JSDOM } from "jsdom";
import type { NextPage } from "next";
import Link from "next/link";
import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Flex } from "../components/Grid";
import {
  Body,
  Caption1,
  Headline,
  LargeTitle,
  fontWeightSemiBold,
} from "../components/Typography";
import { Button } from "../components/buttons/Button";
import { GradientSpinner } from "../components/progress/Spinner";
import { Creator, DropEntry } from "../dropTypes";
import useAnalytics from "../hooks/analytics";
import { useAuthState } from "../hooks/auth";
import { CreatorThumbnail } from "../layouts/creator/creatorThumbnail";
import { PageLayout, PageTypes } from "../layouts/page";
import {
  fetchFeaturedCreators,
  fetchFeaturedDrops,
  fetchSpotlightDrops,
  maybeProcessStoredExternalWalletAddress,
  storeExternalWalletAddressToLocal,
} from "../lib/backend";
import {
  DESCRIPTION,
  DROP_PAGE_SIZE,
  WEBFLOW_HOMEPAGE,
} from "../lib/constants";

import { ImageCarousel } from "../components/ImageCarousel";
import { SortBy } from "../components/SortBy";
import { DropThumbnail } from "../layouts/drop/dropThumbnail";
import { Placeholder } from "../layouts/profile/placeholder";
import {
  hideAboveMobile,
  hideOnMobile,
  mediumScreenAndBelow,
  xSmallScreen,
} from "../lib/mediaQuery";
import { Filter, filterPrettifier, filters } from "../utils/filter";
import { SortOption, sortOptions } from "../utils/sortBy";
import { useRouter } from "next/router";

interface DiscoverProps {
  html: string;
}

const Discover: NextPage<DiscoverProps> = ({ html }) => {

  const router = useRouter();
  const { externalWalletAddress } = router.query;

  storeExternalWalletAddressToLocal(externalWalletAddress?.toString());
  maybeProcessStoredExternalWalletAddress();

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);
  const [selectedSortBy, setSelectedSortBy] = useState<SortOption>(
    sortOptions[0]
  );

  const { data, fetchNextPage, status, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ["filteredFeatureDrops", selectedFilters, selectedSortBy],
      queryFn: ({ pageParam = "" }) =>
        fetchFeaturedDrops({
          filters: selectedFilters,
          sortBy: selectedSortBy,
          lastSlug: pageParam,
        }),
      getNextPageParam: (lastPage) => {
        return lastPage[lastPage.length - 1]?.slug;
      },
      refetchOnWindowFocus: false,
      enabled: typeof window !== "undefined",
    });

  const creatorsQuery = useQuery(["featuredCreators"], fetchFeaturedCreators);
  const spotlightQuery = useQuery(["spotlightDrops"], () =>
    fetchSpotlightDrops({})
  );
  const analytics = useAnalytics();
  const isTracked = useRef(false);
  const auth = useAuthState();
  const showShowMore: boolean =
    data?.pages[data?.pages?.length - 1].length === DROP_PAGE_SIZE;

  React.useEffect(() => {
    if (
      isTracked.current === false &&
      analytics.isReady &&
      auth.loading === false
    ) {
      analytics.trackEvent("viewed_home_page", {
        authenticated: Boolean(auth.user),
        user: auth?.user?.uid,
      });
      isTracked.current = true;
    }
  }, [analytics, auth]);

  React.useEffect(() => {
    const savedFilters: Filter[] = JSON.parse(
      localStorage.getItem("dropFilters")
    );
    const savedSortBy: SortOption = JSON.parse(
      localStorage.getItem("dropSortBy")
    );
    if (savedFilters) {
      setSelectedFilters(savedFilters);
    }
    if (savedSortBy) {
      setSelectedSortBy(savedSortBy);
    }
  }, []);

  const allFilters = [
    { name: "fantasy_manager", value: "true" },
    ...filters.types.map((type) => {
      return { name: "type", value: type };
    }),
    ...filters.genres.map((genre) => {
      return { name: "genre", value: genre };
    }),
  ];

  const handleFilterClick = (filterToAddOrRemove: Filter) => {
    const isFilterAlreadySelected = selectedFilters.some(
      (filter: Filter) =>
        filter.name === filterToAddOrRemove.name &&
        filter.value === filterToAddOrRemove.value
    );

    const updatedFilters: Filter[] | [] = isFilterAlreadySelected
      ? selectedFilters.filter(
          (filter) => filter.value !== filterToAddOrRemove.value
        )
      : [...selectedFilters, filterToAddOrRemove];

    localStorage.setItem("dropFilters", JSON.stringify(updatedFilters));

    setSelectedFilters(updatedFilters);
  };

  const handleSortByClick = (sortOption: SortOption) => {
    localStorage.setItem("dropSortBy", JSON.stringify(sortOption));
    setSelectedSortBy(sortOption);
  };

  const title = `Collect Rare, Limited Edition Music`;
  const subtitle = DESCRIPTION;

  return (
    <PageLayout pageType={PageTypes.NORMAL} backgroundType={"blackout"}>
      <Flex
        direction={"column"}
        align={"center"}
        justify={"center"}
        css={`
          text-align: center;
        `}
      >
        <LargeTitle css={hideOnMobile}>{title}</LargeTitle>
        <Headline css={hideAboveMobile}>{title}</Headline>
        <Body css={hideOnMobile}>{subtitle}</Body>
        <Caption1 css={hideAboveMobile}>{subtitle}</Caption1>
      </Flex>
      <Content>
        <Flex
          css={`
            margin: var(--spacing-lg) 0;
          `}
          direction={"column"}
        >
          {spotlightQuery.data && <ImageCarousel drops={spotlightQuery.data} />}
        </Flex>
        <Flex justify={"space-between"} align={"baseline"} paddingBottom={"xs"}>
          <LargeTitle css={hideOnMobile}>Featured Drops</LargeTitle>
          <Headline css={hideAboveMobile}>Featured Drops</Headline>
          <SortBy
            setSelectedSortBy={handleSortByClick}
            selectedSortBy={selectedSortBy}
            sortOptions={sortOptions}
          />
        </Flex>
        <FiltersContainer>
          {allFilters?.map((filter) => (
            <FilterChip
              onClick={() => handleFilterClick(filter)}
              data-is-selected={selectedFilters?.some(
                (f) => f.value === filter.value
              )}
            >
              <FilterChipText>{filterPrettifier(filter.value)}</FilterChipText>
            </FilterChip>
          ))}
        </FiltersContainer>
        <>
          {status === "loading" ? (
            <Flex
              align="center"
              justify="center"
              width={"100%"}
              css={`
                height: 440px;
              `}
            >
              <GradientSpinner size={100} />
            </Flex>
          ) : data?.pages[0]?.length > 0 ? (
            <DropThumbnailList>
              {data?.pages[0]?.length > 0 &&
                data.pages?.map((drops, i) =>
                  drops.map((drop: DropEntry) => (
                    <DropThumbnail
                      key={drop.id}
                      drop={drop}
                      href={`/drop/${drop.data.slug}`}
                    />
                  ))
                )}
            </DropThumbnailList>
          ) : status === "error" ? (
            <Placeholder
              buttonCallback={refetch}
              variant={"landingPageError"}
            />
          ) : (
            <Placeholder
              variant={"landingPage"}
              buttonCallback={() => {
                localStorage.removeItem("dropFilters");
                setSelectedFilters([]);
              }}
            />
          )}
        </>
        {showShowMore && (
          <ShowMore
            shape={"pill"}
            variant={"semi-transparent"}
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
          >
            Show More
          </ShowMore>
        )}
        <FeaturedArtistHeader />
        <CreatorThumbnailList>
          {creatorsQuery.isLoading ? (
            <Flex align="center" justify="center">
              <GradientSpinner size={100} />
            </Flex>
          ) : (
            creatorsQuery?.data?.map((creator: Creator) => (
              <CreatorThumbnail key={creator.slug} creator={creator} />
            ))
          )}
        </CreatorThumbnailList>
      </Content>
    </PageLayout>
  );
};

const ShowMore = styled(Button)`
  width: 300px;
  margin: auto;
  :hover {
    background: var(--filter-chip-bg-active);
    color: var(--dark-color);
  }
`;

const Retry = styled(ShowMore)``;

const FiltersContainer = styled(Flex)`
  width: 100%;
  gap: 4px;
  padding: var(--spacing-md) 0 var(--spacing-xs);
  flex-wrap: wrap;
  ${xSmallScreen(
    css`
      gap: 2px;
      padding: var(--spacing-xs) 0 0;
    `
  )}
`;

const FilterChip = styled(Flex)`
  cursor: pointer;
  background: var(--filter-chip-bg);
  border: 1px solid var(--filter-chip-border);
  border-radius: 100px;
  padding: var(--spacing-xs) var(--spacing-md);
  &[data-is-selected="true"] {
    background: var(--filter-chip-bg-active);
  }
  @media (pointer: fine) {
    :hover {
      background: var(--filter-chip-bg-hover);
    }
  }
`;

const FilterChipText = styled(Headline)`
  ${xSmallScreen(css`
    font-size: 13px;
    ${fontWeightSemiBold};
    line-height: 19px;
  `)};
  color: rgb(var(--white-light));

  ${FilterChip}[data-is-selected="true"] & {
    color: var(--dark-color);
  }

  ${FilterChip}:not([data-is-selected="true"]):hover & {
    color: rgb(var(--white-light));
  }

  ${FilterChip}[data-is-selected="true"]:hover & {
    color: var(--dark-color);
  }
`;

export default Discover;

export async function getStaticProps() {
  const response = await fetch(WEBFLOW_HOMEPAGE);
  const data = await response;
  const html = await data.text();
  const document = new JSDOM(html).window.document;
  const body = document.body.outerHTML;
  const title = document.title.toString();
  const stylesheets = [document.querySelector('link[rel="stylesheet"]').href];

  return { props: { html: { body, title, stylesheets } } };
}

export const FeaturedArtistHeader = () => {
  return (
    <Flex
      justify={"space-between"}
      align={"baseline"}
      css={`
        margin-top: var(--spacing-lg);
      `}
    >
      <>
        <LargeTitle css={hideOnMobile}>Featured Artists</LargeTitle>
        <Headline css={hideAboveMobile}>Featured Artists</Headline>
      </>
      <Link
        href={"/artists"}
        css={`
          :hover {
            text-decoration: none;
          }
        `}
      >
        <Button variant={"outline"} shape={"rounded-slim"} fontSizeXs={13}>
          See All
        </Button>
      </Link>
    </Flex>
  );
};

export const DropThumbnailList = styled.div<{ lgAlign?: string }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: var(--spacing-md);
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
  justify-content: center;
  margin-bottom: var(--spacing-sm);
  padding-top: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  ${mediumScreenAndBelow(css`
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  `)}
  ${xSmallScreen(css`
    grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
    gap: var(--spacing-xs);
  `)}
`;

export const CreatorThumbnailList = styled.div<{ lgAlign?: string }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 30px;
  justify-content: center;
  margin-bottom: var(--spacing-xl);
  padding-top: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  ${xSmallScreen(css`
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: var(--spacing-xs);
  `)}
`;

export const InnerPageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const Content = styled.div`
  width: 100%;
`;
